import React, { useEffect, useMemo, useState } from "react";
import API_BASE_URL from 'config/api';
import {
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { BlueButton } from "styles";
import { useLocation } from "react-router-dom";
import { WhiteButton } from "styles";
import { fetchIntegrationReadiness } from "services/integrationReadiness";

const USER_DATA_KEY = "userMeData";
const COOKIE_DATA_KEY = "userCookies";
const DEFAULT_CATEGORIES = [
  "necessary",
  "analytics",
  "marketing",
  "functionality",
  "uncategorized",
];

const CATEGORY_DESCRIPTIONS = {
  necessary:
    "Bu çerezler web sitesinin temel işlevlerini çalıştırmak için gereklidir. Bu çerezler olmadan web sitesi düzgün çalışmaz.",
  analytics:
    "Bu çerezler web sitesinin ziyaretçi trafiğini ve kullanım istatistiklerini toplamak için kullanılır.",
  marketing:
    "Bu çerezler reklam ve pazarlama amaçlıdır, kullanıcıların ilgi alanlarına göre kişiselleştirilmiş reklamlar sunmak için kullanılır.",
  // preferences:
  //   "Bu çerezler kullanıcı tercihlerini (dil, bölge vb.) hatırlamak için kullanılır.",
  functionality:
    "Bu çerezler gelişmiş işlevsellik sağlamak için kullanılır (video oynatıcılar, sosyal medya entegrasyonları vb.).",
  uncategorized: "Henüz kategorize edilmemiş çerezler bu bölümde yer alır.",
};

const LANGUAGE_LABELS = { en: "İngilizce", tr: "Türkçe" };

const createEmptyCookie = (category = "necessary", languages = ["en"]) => ({
  name: "",
  category,
  domain: "",
  provider: "",
  translations: Object.fromEntries(
    languages.map((language) => [language, { description: "", duration: "" }])
  ),
});

const CookieCategories = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const [cookieData, setCookieData] = useState({});
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedCategory, setSelectedCategory] = useState("necessary");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newCookie, setNewCookie] = useState(() => createEmptyCookie());
  const [siteLanguages, setSiteLanguages] = useState([]);
  const [editMode, setEditMode] = useState(null);
  const [editingCookie, setEditingCookie] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveCommitted, setSaveCommitted] = useState(false);
  const location = useLocation();

  // Styling constants
  const formControlStyles = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.375rem",
      background: "#fafbfc",
      fontSize: 16,
      padding: "4px 12px",
      "& .MuiSelect-icon": {
        color: "#000",
        right: "12px",
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
      color: "#455a64",
      fontSize: 15,
    },
  };

  const selectStyles = {
    minHeight: 48,
    fontSize: 16,
    borderRadius: "12px",
    ".MuiSelect-select": {
      padding: "14px 32px 14px 12px",
    },
    "& .MuiSelect-icon": {
      display: "block !important",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        borderRadius: "12px",
        marginTop: "8px",
        boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
      },
    },
  };

  // Helper functions
  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  const getCachedCookieData = () => {
    const cachedData = sessionStorage.getItem(COOKIE_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : {};
  };

  const updateSessionCookieData = (updatedData) => {
    sessionStorage.setItem(COOKIE_DATA_KEY, JSON.stringify(updatedData));
    setCookieData(updatedData);
  };

  const loadUserData = async () => {
    try {
      const cachedData = getCachedUserData();
      if (cachedData) return cachedData;

      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (response.ok && data) {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
        return data;
      }
      throw new Error("Kullanıcı verisi alınamadı");
    } catch (err) {
      setFatalError(err.message);
      return null;
    }
  };

  const loadCookieData = async (tenantId) => {
    const response = await fetch(
      `${API_BASE_URL}/cookie-definition/all?tenantId=${tenantId}`,
      {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Çerez verileri alınamadı");
    }

    const data = await response.json();
    updateSessionCookieData(data);
    return data;
  };

  // Initial data loading
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // First try to get everything from session storage
        const cachedUserData = getCachedUserData();
        const cachedCookieData = getCachedCookieData();

        if (cachedUserData && Object.keys(cachedCookieData).length > 0) {
          setUserData(cachedUserData);
          setCookieData(cachedCookieData);

          const client = cachedUserData.user.clients?.[0];
          if (client?.sites?.length > 0) {
            const searchParams = new URLSearchParams(location.search);
            const siteId = searchParams.get("siteId") || client.sites[0].siteId;
            const lang = searchParams.get("lang") || "en";

            setSelectedSite(siteId);
            setSelectedLanguage(lang);
            setNewCookie((prev) => ({ ...prev, category: selectedCategory }));
          }
          return;
        }

        // If no cached data, fetch from API
        const user = await loadUserData();
        if (!user) return;

        setUserData(user);
        const client = user.user.clients?.[0];
        if (!client) return;

        await loadCookieData(client.clientId);

        const searchParams = new URLSearchParams(location.search);
        const siteId = searchParams.get("siteId") || client.sites[0].siteId;
        const lang = searchParams.get("lang") || "en";

        setSelectedSite(siteId);
        setSelectedLanguage(lang);
        setNewCookie((prev) => ({ ...prev, category: selectedCategory }));
      } catch (err) {
        setFatalError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  useEffect(() => {
    if (!selectedSite) {
      setSiteLanguages([]);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    fetchIntegrationReadiness(
      selectedSite,
      sessionStorage.getItem("authToken"),
      controller.signal
    )
      .then((readiness) => {
        if (!cancelled) setSiteLanguages(readiness.integration.bannerLanguages);
      })
      .catch((readinessError) => {
        if (!cancelled && readinessError.name !== "AbortError") setSiteLanguages([]);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedSite]);

  const availableSiteLanguages = useMemo(() => {
    if (siteLanguages.length > 0) return [...new Set(siteLanguages)];
    const catalogLanguages = Object.keys(cookieData[selectedSite] || {});
    if (catalogLanguages.length > 0) return catalogLanguages;
    return selectedLanguage ? [selectedLanguage] : [];
  }, [cookieData, selectedLanguage, selectedSite, siteLanguages]);

  useEffect(() => {
    if (
      availableSiteLanguages.length > 0 &&
      !availableSiteLanguages.includes(selectedLanguage)
    ) {
      setSelectedLanguage(availableSiteLanguages[0]);
    }
  }, [availableSiteLanguages, selectedLanguage]);

  // Data getters
  const getCookiesForCategory = () => {
    if (!selectedSite || !selectedLanguage || !selectedCategory) return [];
    return (
      cookieData[selectedSite]?.[selectedLanguage]?.[selectedCategory] || []
    );
  };

  const getObservedCookieDomain = (cookie) =>
    cookie?.domain || cookie?.observedDomain || "";

  const getCookieSource = (cookie) =>
    getObservedCookieDomain(cookie) || cookie?.source || "";

  const getPartyLabel = (party) => {
    if (party === "first-party") return "Birinci taraf";
    if (party === "third-party") return "Üçüncü taraf";
    if (party === "unknown") return "Belirsiz";
    return party;
  };

  // API handlers
  const handleAddCookie = async () => {
    if (isSaving || saveCommitted) return;

    let writeCommitted = false;

    try {
      const client = userData?.user.clients?.[0];
      if (!client || !selectedSite) return;
      const translations = availableSiteLanguages.map((language) => ({
        language,
        description: newCookie.translations?.[language]?.description?.trim() || "",
        duration: newCookie.translations?.[language]?.duration?.trim() || "",
      }));
      if (!newCookie.name.trim() || translations.some((item) => !item.description)) {
        throw new Error("Çerez adı ve her dil için açıklama zorunludur.");
      }
      setIsSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/cookie-definition`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            tenantId: client.clientId,
            siteId: selectedSite,
            name: newCookie.name.trim(),
            category: newCookie.category,
            domain: newCookie.domain,
            observedDomain: newCookie.domain,
            provider: newCookie.provider,
            source: newCookie.domain,
            translations,
          }),
        }
      );

      if (response.ok) {
        writeCommitted = true;
        setSaveCommitted(true);
        await loadCookieData(client.clientId);
        setSuccessMessage(
          translations.length > 1
            ? `Çerez ${translations.length} dilde başarıyla eklendi`
            : "Çerez başarıyla eklendi"
        );
        setOpenAddDialog(false);
        setSaveCommitted(false);
        setNewCookie(createEmptyCookie(selectedCategory, availableSiteLanguages));
      } else {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Çerez eklenirken hata oluştu");
      }
    } catch (err) {
      setError(
        writeCommitted
          ? "Çerez kaydedildi ancak katalog yenilenemedi. Sayfayı yenileyin."
          : err.message
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateCookie = async () => {
    if (isSaving || saveCommitted) return;

    let writeCommitted = false;

    try {
      const client = userData?.user.clients?.[0];
      if (!client || !editingCookie || !selectedSite) return;
      setIsSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/cookie-definition/update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            id: editingCookie.id,
            tenantId: client.clientId,
            siteId: selectedSite,
            name: editingCookie.name,
            language: selectedLanguage,
            category: editingCookie.category,
            description: editingCookie.description,
            duration: editingCookie.duration,
            domain: editingCookie.domain,
            observedDomain: editingCookie.domain,
            provider: editingCookie.provider,
            source: editingCookie.source,
          }),
        }
      );

      if (response.ok) {
        writeCommitted = true;
        setSaveCommitted(true);
        await loadCookieData(client.clientId);
        setSuccessMessage(
          `${editingCookie.name} çerezi tüm dillerde güncellendi`
        );
        setEditMode(null);
        setEditingCookie(null);
        setSaveCommitted(false);
      } else {
        throw new Error("Çerez güncellenirken hata oluştu");
      }
    } catch (err) {
      setError(
        writeCommitted
          ? "Çerez güncellendi ancak katalog yenilenemedi. Sayfayı yenileyin."
          : err.message
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCookie = (cookie) => {
    if (saveCommitted) {
      window.location.reload();
      return;
    }
    setEditingCookie({
      ...cookie,
      category: cookie.category || selectedCategory,
      domain: getObservedCookieDomain(cookie),
      observedDomain: getObservedCookieDomain(cookie),
      provider: cookie.provider || "",
    });
    setEditMode(cookie.id);
  };

  // Render loading state
  if (loading && !userData) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Render error state
  if (fatalError) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <Typography color="error">{fatalError}</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const client = userData?.user.clients?.[0];
  const cookies = getCookiesForCategory();

  // Main render
  return (
    <DashboardLayout>

      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Çerez Kategorileri
                </Typography>

                {/* Domain and Language Selection */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={formControlStyles}>
                      <InputLabel>Domain</InputLabel>
                      <Select
                        value={selectedSite || ""}
                        onChange={(e) => setSelectedSite(e.target.value)}
                        label="Domain"
                        sx={selectStyles}
                        MenuProps={menuProps}
                        IconComponent={(props) => (
                          <KeyboardArrowDownIcon {...props} fontSize="medium" />
                        )}
                      >
                        {client?.sites?.map((site) => (
                          <MenuItem key={site.siteId} value={site.siteId}>
                            {site.domain}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* Language Selection: Dropdown if >1, else just show label */}
                    {(() => {
                      // Determine available languages for the selected site
                      const availableLanguages = availableSiteLanguages;
                      // If only one language, show as text
                      if (availableLanguages.length === 1) {
                        return (
                          <Box sx={{ minHeight: 56, display: "flex", alignItems: "center", pl: 1 }}>
                            <Typography variant="body1">
                              {LANGUAGE_LABELS[availableLanguages[0]] || availableLanguages[0]}
                            </Typography>
                          </Box>
                        );
                      }
                      // Multiple languages: show dropdown
                      return (
                        <FormControl fullWidth sx={formControlStyles}>
                          <InputLabel>Dil</InputLabel>
                          <Select
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                            label="Dil"
                            sx={selectStyles}
                            MenuProps={menuProps}
                            IconComponent={(props) => (
                              <KeyboardArrowDownIcon {...props} fontSize="medium" />
                            )}
                          >
                            {availableLanguages.map((lang) => (
                              <MenuItem key={lang} value={lang}>
                                {LANGUAGE_LABELS[lang] || lang}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })()}
                  </Grid>
                </Grid>

                {/* Main Content */}
                <Grid container spacing={3}>
                  {/* Categories List */}
                  <Grid item xs={12} md={3}>
                    <Paper elevation={2}>
                      <List>
                        {DEFAULT_CATEGORIES.map((category) => (
                          <React.Fragment key={category}>
                            <ListItem disablePadding>
                              <ListItemButton
                                selected={selectedCategory === category}
                                onClick={() => {
                                  setSelectedCategory(category);
                                  setNewCookie((prev) => ({
                                    ...prev,
                                    category,
                                  }));
                                }}
                                sx={{
                                  "&.Mui-selected": {
                                    backgroundColor: "aliceblue",
                                    "&:hover": {
                                      backgroundColor: "aliceblue",
                                    },
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    category.charAt(0).toUpperCase() +
                                    category.slice(1)
                                  }
                                  secondary={`${
                                    cookieData[selectedSite]?.[
                                      selectedLanguage
                                    ]?.[category]?.length || 0
                                  } çerez`}
                                  primaryTypographyProps={{
                                    fontWeight: "600",
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                            <Divider sx={{ margin: 0, padding: 0 }} />
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Cookies List */}
                  <Grid item xs={12} md={9}>
                    <Card>
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                          gap={2}
                        >
                          <Box>
                            <Typography variant="h5" fontWeight="bold">
                              {selectedCategory.charAt(0).toUpperCase() +
                                selectedCategory.slice(1)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              mt={1}
                            >
                              {CATEGORY_DESCRIPTIONS[selectedCategory]}
                            </Typography>
                          </Box>
                          <BlueButton
                            sx={{ flexShrink: 0, marginBottom: "auto" }}
                            onClick={() => {
                              if (saveCommitted) {
                                window.location.reload();
                                return;
                              }
                              setNewCookie(createEmptyCookie(
                                selectedCategory,
                                availableSiteLanguages
                              ));
                              setOpenAddDialog(true);
                            }}
                          >
                            Çerez Ekle
                          </BlueButton>
                        </Box>

                        {cookies.length === 0 ? (
                          <Typography
                            variant="body1"
                            sx={{ textAlign: "center", py: 4 }}
                          >
                            Bu kategoride çerez bulunamadı
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {cookies.map((cookie) => (
                              <Grid item xs={12} key={cookie.id}>
                                <Card variant="outlined">
                                  <CardContent>
                                    {editMode === cookie.id ? (
                                      <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                          <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            mb={1}
                                          >
                                            {cookie.name}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <FormControl
                                            fullWidth
                                            sx={formControlStyles}
                                          >
                                            <InputLabel>Kategori</InputLabel>
                                            <Select
                                              value={
                                                editingCookie?.category ||
                                                selectedCategory
                                              }
                                              onChange={(e) =>
                                                setEditingCookie({
                                                  ...editingCookie,
                                                  category: e.target.value,
                                                })
                                              }
                                              label="Kategori"
                                              sx={selectStyles}
                                              MenuProps={menuProps}
                                              IconComponent={(props) => (
                                                <KeyboardArrowDownIcon
                                                  {...props}
                                                  fontSize="medium"
                                                />
                                              )}
                                            >
                                              {DEFAULT_CATEGORIES.map((cat) => (
                                                <MenuItem key={cat} value={cat}>
                                                  {cat.charAt(0).toUpperCase() +
                                                    cat.slice(1)}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <FormControl
                                            fullWidth
                                            sx={formControlStyles}
                                          >
                                            <TextField
                                              fullWidth
                                              label="Kaynak / Domain"
                                              value={
                                                editingCookie?.domain ||
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingCookie({
                                                  ...editingCookie,
                                                  domain: e.target.value,
                                                  observedDomain: e.target.value,
                                                })
                                              }
                                              InputProps={{
                                                sx: {
                                                  "& input": {
                                                    paddingY: "0.55rem", // <input> içine etki eden padding
                                                  },
                                                },
                                              }}
                                            />
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <FormControl
                                            fullWidth
                                            sx={formControlStyles}
                                          >
                                            <TextField
                                              fullWidth
                                              label="Sağlayıcı"
                                              value={
                                                editingCookie?.provider || ""
                                              }
                                              onChange={(e) =>
                                                setEditingCookie({
                                                  ...editingCookie,
                                                  provider: e.target.value,
                                                })
                                              }
                                            />
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                          <FormControl
                                            fullWidth
                                            sx={formControlStyles}
                                          >
                                            <TextField
                                              fullWidth
                                              multiline
                                              rows={3}
                                              label="Açıklama"
                                              value={
                                                editingCookie?.description ||
                                                cookie.description ||
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingCookie({
                                                  ...editingCookie,
                                                  description: e.target.value,
                                                })
                                              }
                                            />
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                          <FormControl
                                            fullWidth
                                            sx={formControlStyles}
                                          >
                                            <TextField
                                              fullWidth
                                              label="Süre"
                                              value={
                                                editingCookie?.duration ||
                                                cookie.duration ||
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingCookie({
                                                  ...editingCookie,
                                                  duration: e.target.value,
                                                })
                                              }
                                            />
                                          </FormControl>
                                        </Grid>
                                        <Grid
                                          item
                                          xs={12}
                                          display="flex"
                                          justifyContent="flex-end"
                                        >
                                          <BlueButton
                                            onClick={handleUpdateCookie}
                                            disabled={isSaving || saveCommitted}
                                            sx={{ mr: 1 }}
                                          >
                                            {isSaving
                                              ? "Kaydediliyor..."
                                              : saveCommitted
                                              ? "Kaydedildi"
                                              : "Kaydet"}
                                          </BlueButton>
                                          <WhiteButton
                                            variant="outlined"
                                            disabled={isSaving}
                                            onClick={() => {
                                              if (saveCommitted) {
                                                window.location.reload();
                                                return;
                                              }
                                              setEditMode(null);
                                              setEditingCookie(null);
                                            }}
                                          >
                                            {saveCommitted ? "Sayfayı Yenile" : "İptal"}
                                          </WhiteButton>
                                        </Grid>
                                      </Grid>
                                    ) : (
                                      <>
                                        <Box
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="flex-start"
                                        >
                                          <Box>
                                            <Typography
                                              variant="h6"
                                              fontWeight="bold"
                                            >
                                              {cookie.name}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              mt={1}
                                              mb={2}
                                            >
                                              {cookie.description}
                                            </Typography>
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleEditCookie(cookie)
                                            }
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                        <Box
                                          display="flex"
                                          justifyContent="space-between"
                                          flexWrap="wrap"
                                          gap={2}
                                        >
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              fontWeight="bold"
                                            >
                                              Kategori:
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              ml={1}
                                            >
                                              {cookie.category ||
                                                selectedCategory}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              fontWeight="bold"
                                            >
                                              Süre:
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              ml={1}
                                            >
                                              {cookie.duration ||
                                                "Belirtilmemiş"}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              fontWeight="bold"
                                            >
                                              Kaynak:
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              ml={1}
                                            >
                                              {getCookieSource(cookie) ||
                                                "Belirtilmemiş"}
                                            </Typography>
                                          </Box>
                                          {cookie.provider && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                fontWeight="bold"
                                              >
                                                Sağlayıcı:
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                ml={1}
                                              >
                                                {cookie.provider}
                                              </Typography>
                                            </Box>
                                          )}
                                          {cookie.party && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                fontWeight="bold"
                                              >
                                                Taraf:
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                ml={1}
                                              >
                                                {getPartyLabel(cookie.party)}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Cookie Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => {
          if (!isSaving && !saveCommitted) setOpenAddDialog(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Yeni Çerez Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={formControlStyles}>
                <TextField
                  fullWidth
                  label="Çerez Adı"
                  value={newCookie.name}
                  onChange={(e) =>
                    setNewCookie({ ...newCookie, name: e.target.value })
                  }
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={formControlStyles}>
                <InputLabel>Kategori</InputLabel>
                <Select
                  value={newCookie.category}
                  onChange={(e) =>
                    setNewCookie({ ...newCookie, category: e.target.value })
                  }
                  label="Kategori"
                  sx={selectStyles}
                  MenuProps={menuProps}
                  IconComponent={(props) => (
                    <KeyboardArrowDownIcon {...props} fontSize="medium" />
                  )}
                  required
                >
                  {DEFAULT_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={formControlStyles}>
                <TextField
                  fullWidth
                  label="Çerez domaini"
                  value={newCookie.domain}
                  helperText="Örn. logicapod.com veya doubleclick.net"
                  onChange={(e) => setNewCookie({ ...newCookie, domain: e.target.value })}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={formControlStyles}>
                <TextField
                  fullWidth
                  label="Sağlayıcı"
                  value={newCookie.provider}
                  helperText="Örn. Google Analytics"
                  onChange={(e) =>
                    setNewCookie({ ...newCookie, provider: e.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Dil içerikleri
              </Typography>
            </Grid>
            {availableSiteLanguages.map((language) => (
              <Grid
                item
                xs={12}
                md={availableSiteLanguages.length > 1 ? 6 : 12}
                key={language}
              >
                <Box sx={{ border: "1px solid", borderColor: "divider", p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                    {LANGUAGE_LABELS[language] || language.toUpperCase()}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Açıklama"
                    value={newCookie.translations?.[language]?.description || ""}
                    onChange={(event) => setNewCookie((current) => ({
                      ...current,
                      translations: {
                        ...current.translations,
                        [language]: {
                          ...current.translations?.[language],
                          description: event.target.value,
                        },
                      },
                    }))}
                    required
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Süre"
                    value={newCookie.translations?.[language]?.duration || ""}
                    onChange={(event) => setNewCookie((current) => ({
                      ...current,
                      translations: {
                        ...current.translations,
                        [language]: {
                          ...current.translations?.[language],
                          duration: event.target.value,
                        },
                      },
                    }))}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <WhiteButton
            disabled={isSaving}
            onClick={() => {
              if (saveCommitted) {
                window.location.reload();
                return;
              }
              setOpenAddDialog(false);
            }}
          >
            {saveCommitted ? "Sayfayı Yenile" : "İptal"}
          </WhiteButton>
          <BlueButton
            disabled={isSaving || saveCommitted}
            onClick={handleAddCookie}
          >
            {isSaving
              ? "Kaydediliyor..."
              : saveCommitted
              ? "Kaydedildi"
              : "Kaydet"}
          </BlueButton>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CookieCategories;
